import './i18n.js'
import { initFirebase, initCrashlytics } from './config/firebase.js'
import Phaser from 'phaser'
import { BootScene } from './game/scenes/BootScene.js'
import { MatchScene } from './game/scenes/MatchScene.js'
import { PackOpenScene } from './game/scenes/PackOpenScene.js'
import React from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.js'
import { Capacitor } from '@capacitor/core'
import { NotificationService } from './services/NotificationService.js'
import { CapacitorPushAdapter } from './adapters/CapacitorPushAdapter.js'
import { NoopNotificationAdapter } from './adapters/NoopNotificationAdapter.js'
import { LocalStorageAdapter } from './adapters/LocalStorageAdapter.js'
import { PostgresPersistenceAdapter } from './adapters/PostgresPersistenceAdapter.js'
import { IAPService } from './services/IAPService.js'
import { AnalyticsService } from './services/AnalyticsService.js'
import { ENV } from './config/env.js'
import type { IPersistence } from '@interfaces/index'
import { useProgressionStore } from './ui/useProgressionStore.js'
import { useNavigationStore } from './ui/useNavigationStore.js'
import { useGameStore, setEventBus, tickRemainingMs } from './ui/useGameStore.js'
import { initUIBridge } from './game/UIBridge.js'
import i18n from './i18n.js'
import { PerformanceMonitor } from './engine/PerformanceMonitor.js'

// ---------------------------------------------------------------------------
// Persistence adapter factory
// Use Postgres when explicitly enabled via env flag or running on a native
// Capacitor platform (where localStorage is unreliable across app updates).
// ---------------------------------------------------------------------------
export function createPersistenceAdapter(): IPersistence {
  if (Capacitor.isNativePlatform() || ENV.USE_POSTGRES) {
    return new PostgresPersistenceAdapter()
  }
  return new LocalStorageAdapter()
}

window.addEventListener('DOMContentLoaded', () => {
  // ---------------------------------------------------------------------------
  // 0. i18n — apply persisted language preference before anything renders.
  // ---------------------------------------------------------------------------
  const storedLanguage = useProgressionStore.getState().language
  void i18n.changeLanguage(storedLanguage)

  // ---------------------------------------------------------------------------
  // 0.5. Firebase — initialise web SDK before Analytics so logEvent works on browser.
  //      On native the @capacitor-firebase plugins handle initialisation automatically.
  // ---------------------------------------------------------------------------
  initFirebase()
  initCrashlytics().catch(() => {})

  // ---------------------------------------------------------------------------
  // 1. Analytics — initialise before Phaser so first-frame events are captured.
  // ---------------------------------------------------------------------------
  AnalyticsService.getInstance().init(Capacitor.isNativePlatform())

  // ---------------------------------------------------------------------------
  // 2. IAP — configure RevenueCat on native; mock falls back automatically on web.
  // ---------------------------------------------------------------------------
  IAPService.init('revenuecat').catch((err) => {
    console.error('[main] IAPService.init failed:', err)
  })

  // ---------------------------------------------------------------------------
  // 3. Notifications
  // ---------------------------------------------------------------------------
  const notifAdapter = Capacitor.isNativePlatform()
    ? new CapacitorPushAdapter()
    : new NoopNotificationAdapter()
  NotificationService.init(notifAdapter)

  // ---------------------------------------------------------------------------
  // 4. Phaser game engine
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 3.5. UIBridge — wire game layer to UI layer before Phaser initialises.
  // Composition root is the only place allowed to import both layers.
  // ---------------------------------------------------------------------------
  initUIBridge({
    setLoading: (p, msg) => useGameStore.getState().setLoading(p, msg),
    setLoadingDone: () => useGameStore.getState().setLoadingDone(),
    setEventBus: (bus, startHp, localPlayerId) => setEventBus(bus, startHp, localPlayerId),
    tickRemainingMs: (ms) => tickRemainingMs(ms),
    getGameMode: () => useNavigationStore.getState().gameMode,
    getMatchmakingMatchId: () => useNavigationStore.getState().matchmakingMatchId ?? null,
    getDeck: (id) => useProgressionStore.getState().decks.find((d) => d.id === id),
    updateQuestProgress: (p) => useProgressionStore.getState().updateQuestProgress(p),
  })

  // Enable engine-level performance tracking in dev mode so Shift+P reports
  // are populated from the first frame.
  if (ENV.IS_DEV) {
    PerformanceMonitor.enable()
  }

  const phaserGame = new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'phaser-container',
    width: window.innerWidth,
    height: window.innerHeight,
    transparent: true,
    antialias: true,
    scene: [BootScene, MatchScene, PackOpenScene],
    scale: {
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    fps: {
      target: 60,
      forceSetTimeOut: false,
    },
  })

  // Expose game instance for React layer (e.g. PackOpenScene launch)
  ;(window as Window & { __phaserGame?: Phaser.Game }).__phaserGame = phaserGame

  // Phaser adds a keydown listener on `window` (bubble phase) and calls
  // preventDefault() for captured keys, which blocks AltGr+Q (@) and other
  // compose sequences when typing in React <input>/<textarea> elements.
  // Running stopPropagation() in the capture phase here fires before Phaser's
  // bubble-phase listener, so text inputs receive all keystrokes unfiltered.
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        e.stopPropagation()
      }
    },
    true,
  )

  // ---------------------------------------------------------------------------
  // 5. React UI overlay
  // ---------------------------------------------------------------------------
  const uiRoot = document.getElementById('ui-overlay')
  if (uiRoot) {
    createRoot(uiRoot).render(React.createElement(App))
  }
})
